import React from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import Box from "@material-ui/core/Box";

// FixedSizeList needs an exact row height, so the five stacked lines and the
// ListItem's own padding are budgeted here rather than measured.
const ROW_HEIGHT = 144;

const useStyles = makeStyles((theme) => ({
  root: {
    flex: "1 1 auto",
  },
  // A percentage width resolves against the row width react-window sets on the
  // ListItem, which is what lets the description truncate rather than stretch
  // the row out to the length of its text.
  itemContent: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    width: "100%",
  },
  listItemTitle: {
    fontWeight: 600,
    marginBottom: 8,
    marginTop: 0,
  },
  listItemContent: {
    color: theme.palette.gray.dark,
    marginTop: 0,
    marginBottom: 0,
  },
  listItemDescription: {
    color: theme.palette.gray.dark,
    fontSize: "0.875rem",
    marginTop: 0,
    marginBottom: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const dateFormat = new Intl.DateTimeFormat("default", {
  dateStyle: "short",
  timeStyle: "short",
});

function InspectionListCollapsed(props) {
  const { snapshot, onItemSelected } = props;
  const { selectedId } = useParams();

  const classes = useStyles();

  // Filter out hidden inspections
  const visibleDocs = snapshot?.docs.filter(doc => !doc.data().isHidden) ?? [];

  const renderRow = (props) => {
    // eslint-disable-next-line react/prop-types
    const { index, style } = props;

    const doc = visibleDocs[index];
    if (!doc) return null;

    const data = doc.data();

    const selected = doc.id == selectedId;

    return (
      <ListItem
        button
        style={style}
        key={doc.id}
        selected={selected}
        onClick={() => onItemSelected(doc.id)}
        divider
      >
        <div className={classes.itemContent}>
          <p className={classes.listItemTitle}>{data.location}</p>
          <p className={classes.listItemContent}>
            {`${data.city}, ${data.state}`}
          </p>
          <p className={classes.listItemContent}>{`${data.inspector}`}</p>
          <p className={classes.listItemContent}>
            {`${dateFormat.format(data.createdAt?.toDate())}`}
          </p>
          {data.jobDescription && (
            <p
              className={classes.listItemDescription}
              title={data.jobDescription}
            >
              {data.jobDescription}
            </p>
          )}
        </div>
      </ListItem>
    );
  };

  return (
    <Box className={classes.root}>
      <AutoSizer>
        {({ height, width }) => (
          <FixedSizeList
            height={height}
            width={width}
            itemCount={visibleDocs.length}
            itemSize={ROW_HEIGHT}
          >
            {renderRow}
          </FixedSizeList>
        )}
      </AutoSizer>
    </Box>
  );
}

InspectionListCollapsed.propTypes = {
  snapshot: PropTypes.object,
  selectedId: PropTypes.string,
  onItemSelected: PropTypes.func,
};

export default InspectionListCollapsed;
